import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import Link from 'next/link';

import { IWorkersTimesheet } from '@/modules/workforce/workersTimesheet/types';
import {
  Avatar,
  Button,
  NextImage,
  Typography,
} from '@/shared/components/atoms';
import routes from '@/shared/constant/routes';
import { getLocalizedDate } from '@/shared/lib/dates';

import CameraIcon from '~/images/shared/camera.svg';
import VerifyIcon from '~/images/shared/clipboard-tick.svg';

const columnHelper =
  createColumnHelper<IWorkersTimesheet.IAttendanceTimesheetItem>();

export const timesheetTableColumns = (
  t: TFunction,
  handleWorkerClick: (url: string) => void,
  onClickVerifyHandler: (
    row: IWorkersTimesheet.IAttendanceTimesheetItem,
    action: 'show' | 'review'
  ) => void,
  locale?: string
) => [
  columnHelper.accessor('workerName', {
    cell: ({ getValue, row: { original } }) => {
      return (
        <div
          onClick={() =>
            handleWorkerClick(
              `${routes.suppliers}/${original.supplierId}/worker/${original?.workerId}`
            )
          }
          className='cursor-pointer'
        >
          <div className='flex items-center  gap-8  '>
            <div className='overflow-hidden rounded-full bg-background-disable'>
              {original?.profilePicture ? (
                <NextImage
                  useSkeleton
                  fill
                  src={original?.profilePicture || ''}
                  alt={getValue()}
                  className='h-40 w-40 '
                />
              ) : (
                <Avatar size='md' variant='single' />
              )}
            </div>
            {getValue()}
          </div>
        </div>
      );
    },
    header: () => t('name'),
  }),
  columnHelper.accessor('attendance.isFaceVerified', {
    cell: ({ getValue, row: { original } }) =>
      // ************ VERIFIED *************
      getValue() && original.dismissal.isFaceVerified ? (
        <Typography
          variant='caption'
          weight='bold'
          className='inline-block rounded-xl bg-secondary-90 px-16 py-4'
        >
          {t('verified-identity')}
        </Typography>
      ) : // ************ FRAUD *************
      checkIsFraud(original.attendance.isFraud, original.dismissal.isFraud) ? (
        <Typography
          variant='caption'
          weight='bold'
          className='inline-block rounded-xl bg-error-90 px-16 py-4'
        >
          {t('fraud')}
        </Typography>
      ) : // ************ SUSPICIOUS *************
      checkIsSuspicious(getValue(), original.dismissal.isFaceVerified) ? (
        <Typography
          variant='caption'
          weight='bold'
          className='inline-block rounded-xl bg-neutralVariant-96 px-16 py-4 '
        >
          {t('suspicious')}
        </Typography>
      ) : (
        '-'
      ),
    header: () => t('face-id-verification'),
  }),
  columnHelper.accessor('nationalIdNumber', {
    cell: ({ getValue }) => getValue() || '-',
    header: () => t('worker-national-id'),
  }),
  columnHelper.accessor('customId', {
    cell: ({ getValue }) => getValue() || '-',
    header: () => t('company-code'),
  }),
  columnHelper.accessor('supplierName', {
    cell: ({ getValue, row: { original } }) => (
      <Link href={`${routes?.suppliers}/${original?.supplierId}`}>
        {getValue() || '-'}
      </Link>
    ),
    header: () => t('teams'),
  }),
  columnHelper.accessor('attendance.time', {
    cell: ({ getValue }) =>
      getLocalizedDate({
        date: getValue(),
        formatDate: 'hh:mm a',
        locale,
      }) || '-',
    header: () => t('check-in'),
  }),
  columnHelper.accessor('dismissal.time', {
    cell: ({ getValue }) =>
      getLocalizedDate({
        date: getValue(),
        formatDate: 'hh:mm a',
        locale,
      }) || '-',
    header: () => t('check-out'),
  }),
  columnHelper.accessor('duration', {
    cell: ({ getValue }) => getValue() || '-',
    header: () => t('hours-number'),
  }),
  columnHelper.accessor('dateUploaded', {
    cell: ({ getValue }) => getValue() || '-',
    header: () => t('upload-date'),
  }),

  columnHelper.accessor('attendance.isFaceVerified', {
    cell: ({ getValue, row: { original } }) =>
      !checkIsFraud(original.attendance.isFraud, original.dismissal.isFraud) &&
      checkIsSuspicious(getValue(), original.dismissal.isFaceVerified) ? (
        <Button
          size='none'
          onClick={() => onClickVerifyHandler(original, 'review')}
        >
          <div className='flex h-36 w-100 items-center justify-center gap-8   capitalize'>
            <span className='stroke-white text-20'>
              <VerifyIcon />
            </span>
            {t('review-identity')}
          </div>
        </Button>
      ) : (
        <Button
          color='secondary'
          size='none'
          onClick={() => onClickVerifyHandler(original, 'show')}
        >
          <div className='flex h-36 w-100 items-center justify-center   gap-8  capitalize'>
            <span className='text-20'>
              <CameraIcon />
            </span>
            {t('show-identity')}
          </div>
        </Button>
      ),
    header: () => t('captured-face-ID'),
  }),
];

interface ICheckProps {
  clockInVerification: 0 | 1;
  clockOutVerification: 0 | 1;
}
export const checkIsVerified = ({
  clockInVerification,
  clockOutVerification,
}: ICheckProps) => {
  return clockInVerification && clockOutVerification;
};
export const checkIsSuspicious = (
  clockInVerification: 0 | 1,
  clockOutVerification: 0 | 1
) => {
  return clockInVerification === 0 || clockOutVerification === 0;
};
export const checkIsFraud = (isFraudClockin: 0 | 1, isFraudClockOut: 0 | 1) => {
  return isFraudClockin === 1 || isFraudClockOut === 1;
};
